import React from 'react';

import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { LocaleContext } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';
import { NfTransactionDetailsResponse } from '@/types/simulation';
import { FeeType, FixedFee, PercentageFee, SpreadFee } from '@/types/transaction';
import { formatAmount, FormatStyle } from '@/utility/formatAmount';

export interface TransactionDetailsTextValues {
  readonly grossAmountToConvert: string;
  readonly fixedFees: ReadonlyArray<FixedFee>;
  readonly percentageFeesWithoutTaxes: ReadonlyArray<PercentageFee>;
  readonly totalFeesValue: string;
  readonly totalAmountToConvert: string | null;
  readonly conversionRate: number | null;
  readonly marketExchangeRate: number | null;
  readonly calizaExchangeSpreadFeePercentage: string | null;
  readonly integratorRate: number | null;
  readonly integratorSpreadFeePercentage: string | null;
  readonly effectiveTransactionValue: number | null;
}

export const useTransactionDetails = (
  transactionDetails: NfTransactionDetailsResponse,
  fromAmount: number | null,
  fromCurrency: CurrencyCodeType,
): TransactionDetailsTextValues => {
  const percentageFormatter = useLocalizedFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'percent',
  });

  const currencyFormatter = useCurrencyFormatter();
  const locale = React.useContext<Locale>(LocaleContext);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();

  return React.useMemo((): TransactionDetailsTextValues => {
    const { conversionDetails, feeDetails } = transactionDetails;
    const { totalFees, fixedFees, percentageFees } = feeDetails;
    const percentageFeesWithoutTaxes = percentageFees.filter(
      (fee: PercentageFee): boolean => fee.type !== FeeType.TAX,
    );

    const nonNullAmount = fromAmount ?? 0;
    const grossAmountToConvert = currencyFormatter.format(nonNullAmount, fromCurrency);
    const totalFeesCurrency = totalFees.currencyCode === '' ? fromCurrency : totalFees.currencyCode;
    const totalFeesValue = currencyFormatter.format(totalFees.value, totalFeesCurrency);

    const fixedFeesFormatted: ReadonlyArray<FixedFee> = fixedFees.map(
      (fee: FixedFee): FixedFee => ({
        ...fee,
        value: formatAmount(
          locale,
          Number(fee.value),
          fee.currencyCode,
          FormatStyle.extended,
          2,
          displayUsdcAsUsd,
        ),
      }),
    );

    const percentageFeesWithoutTaxesFormatted: ReadonlyArray<PercentageFee> =
      percentageFeesWithoutTaxes.map(
        (fee: PercentageFee): PercentageFee => ({
          ...fee,
          calculatedFee: formatAmount(
            locale,
            Number(fee.calculatedFee),
            fee.feeCurrency,
            FormatStyle.extended,
            2,
            displayUsdcAsUsd,
          ),
          percentageRate: percentageFormatter.format(Number(fee.percentageRate) / 100),
        }),
      );

    const integratorSpreadFee = feeDetails.spreadFees.find(
      (fee: SpreadFee): boolean => fee.type === FeeType.INTEGRATOR_SPREAD,
    );
    const integratorSpreadFeePercentage = integratorSpreadFee
      ? percentageFormatter.format(Number(integratorSpreadFee.spreadBasisPoint))
      : null;

    if (conversionDetails) {
      const {
        clientRate: clientExchangeRate,
        calizaRate: calizaExchangeSpreadFeePercentage,
        marketExchangeRate,
        amountToBeConverted,
        effectiveTransactionValue,
        integratorRate,
      } = conversionDetails;

      const totalAmountToConvert = amountToBeConverted
        ? formatAmount(
            locale,
            amountToBeConverted.value,
            amountToBeConverted.currencyCode,
            FormatStyle.extended,
            2,
            displayUsdcAsUsd,
          )
        : '';

      const exchangeRate = clientExchangeRate ?? marketExchangeRate;

      const conversionRateValue = Number(exchangeRate);

      const calizaExchangeSpreadFeePercentageFormatted = percentageFormatter.format(
        Number(calizaExchangeSpreadFeePercentage) / 100,
      );

      const effectiveTransactionValueNumber = Number(effectiveTransactionValue);

      const marketExchangeRateNumber =
        marketExchangeRate && Number(marketExchangeRate) !== 0 ? Number(marketExchangeRate) : null;

      const integratorRateNumber =
        integratorRate != null && Number(integratorRate) !== 0 ? Number(integratorRate) : null;

      return {
        grossAmountToConvert: grossAmountToConvert,
        fixedFees: fixedFeesFormatted,
        percentageFeesWithoutTaxes: percentageFeesWithoutTaxesFormatted,
        totalFeesValue: totalFeesValue,
        totalAmountToConvert: totalAmountToConvert,
        conversionRate: Number.isNaN(conversionRateValue) ? null : conversionRateValue,
        marketExchangeRate: Number.isNaN(marketExchangeRateNumber)
          ? null
          : marketExchangeRateNumber,
        integratorRate:
          integratorRateNumber != null && !Number.isNaN(integratorRateNumber)
            ? integratorRateNumber
            : null,
        integratorSpreadFeePercentage,
        calizaExchangeSpreadFeePercentage: calizaExchangeSpreadFeePercentageFormatted,
        effectiveTransactionValue: Number.isNaN(effectiveTransactionValueNumber)
          ? null
          : effectiveTransactionValueNumber,
      };
    } else {
      return {
        grossAmountToConvert: grossAmountToConvert,
        fixedFees: fixedFeesFormatted,
        percentageFeesWithoutTaxes: percentageFeesWithoutTaxesFormatted,
        totalFeesValue: totalFeesValue,
        totalAmountToConvert: null,
        conversionRate: null,
        marketExchangeRate: null,
        integratorRate: null,
        integratorSpreadFeePercentage,
        calizaExchangeSpreadFeePercentage: null,
        effectiveTransactionValue: null,
      };
    }
  }, [
    currencyFormatter,
    displayUsdcAsUsd,
    fromAmount,
    fromCurrency,
    locale,
    percentageFormatter,
    transactionDetails,
  ]);
};
